"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { logger } from "@/lib/logger";
import toast from "react-hot-toast";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { AvatarUpload } from "@/components/avatar-upload";
import {
  ArrowLeft,
  Loader2,
  Save,
  Wrench,
  Clock,
  MapPin,
  Calendar,
  Shield,
  Sparkles,
  User,
} from "lucide-react";
import { TRADES } from "@/lib/constants";

const EXPERIENCE_OPTIONS = [
  { label: "Less than 1 year", value: 0 },
  { label: "1-3 years", value: 2 },
  { label: "3-5 years", value: 4 },
  { label: "5-10 years", value: 7 },
  { label: "10+ years", value: 12 },
];

const AVAILABILITY_OPTIONS = [
  { label: "Full-time", value: "full-time" as const, desc: "Mon-Fri, regular hours" },
  { label: "Casual", value: "casual" as const, desc: "As needed, flexible" },
  { label: "Weekends", value: "weekends" as const, desc: "Saturday & Sunday" },
  { label: "Flexible", value: "flexible" as const, desc: "Any time, any days" },
];

export default function EditProfilePage() {
  const router = useRouter();
  const supabase = createClient();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile data
  const [userName, setUserName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [bio, setBio] = useState("");
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [experience, setExperience] = useState<number | null>(null);
  const [suburb, setSuburb] = useState("");
  const [address, setAddress] = useState("");
  const [abn, setAbn] = useState("");
  const [whiteCardNumber, setWhiteCardNumber] = useState("");
  const [whiteCardExpiry, setWhiteCardExpiry] = useState("");
  const [availability, setAvailability] = useState<string | null>(null);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [newCert, setNewCert] = useState("");

  useEffect(() => {
    async function loadProfile() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.push("/auth/login");
        return;
      }

      const [profileRes, workerRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("name, avatar_url, suburb, address")
          .eq("id", user.id)
          .single(),
        supabase
          .from("worker_profiles")
          .select("trades, experience_years, certifications, bio, availability, abn, white_card_number, white_card_expiry")
          .eq("profile_id", user.id)
          .single(),
      ]);

      if (profileRes.data) {
        setUserName(profileRes.data.name || "");
        setAvatarUrl(profileRes.data.avatar_url);
        setSuburb(profileRes.data.suburb || "");
        setAddress(profileRes.data.address || "");
      }

      if (workerRes.data) {
        setBio(workerRes.data.bio || "");
        setSelectedTrades(workerRes.data.trades || []);
        setExperience(workerRes.data.experience_years);
        setAvailability(workerRes.data.availability);
        setCertifications(workerRes.data.certifications || []);
        setAbn(workerRes.data.abn || "");
        setWhiteCardNumber(workerRes.data.white_card_number || "");
        setWhiteCardExpiry(workerRes.data.white_card_expiry || "");
      }

      setLoading(false);
    }
    loadProfile();
  }, [supabase, router]);

  function toggleTrade(trade: string) {
    setSelectedTrades((prev) =>
      prev.includes(trade) ? prev.filter((t) => t !== trade) : [...prev, trade]
    );
  }

  function addCert() {
    const trimmed = newCert.trim();
    if (trimmed && !certifications.includes(trimmed)) {
      setCertifications((prev) => [...prev, trimmed]);
      setNewCert("");
    }
  }

  function removeCert(cert: string) {
    setCertifications((prev) => prev.filter((c) => c !== cert));
  }

  async function handleSave() {
    if (!userName.trim()) {
      toast.error("Enter your name");
      return;
    }
    if (selectedTrades.length === 0) {
      toast.error("Select at least one trade");
      return;
    }
    if (!suburb.trim()) {
      toast.error("Enter your suburb");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const [profileUpdate, workerUpdate] = await Promise.all([
        supabase
          .from("profiles")
          .update({
            name: userName.trim(),
            suburb: suburb.trim(),
            address: address.trim() || null,
          })
          .eq("id", user.id),
        supabase
          .from("worker_profiles")
          .update({
            bio: bio.trim(),
            trades: selectedTrades,
            experience_years: experience,
            availability,
            certifications,
            abn: abn.trim() || null,
            white_card_number: whiteCardNumber.trim() || null,
            white_card_expiry: whiteCardExpiry || null,
          })
          .eq("profile_id", user.id),
      ]);

      if (profileUpdate.error) throw profileUpdate.error;
      if (workerUpdate.error) throw workerUpdate.error;

      toast.success("Profile updated!");
      router.push("/worker/profile");
      router.refresh();
    } catch (error) {
      logger.error("Save error:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-28">
      <main className="mx-auto max-w-screen-sm px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.back()}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Edit Profile</h1>
        </div>

        {/* Avatar */}
        <div className="flex justify-center mb-8">
          <AvatarUpload currentUrl={avatarUrl} userName={userName} />
        </div>

        {/* Name */}
        <div className="mb-6">
          <Label htmlFor="name" className="flex items-center gap-2 text-base font-semibold mb-2">
            <User className="h-4 w-4 text-primary" />
            Full Name
          </Label>
          <Input
            id="name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="e.g. Michael McLoughlin"
            className="h-14 text-base"
          />
        </div>

        {/* Bio */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            About Me
          </Label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={4}
            className="w-full rounded-xl border-2 border-border bg-background px-4 py-3 text-base focus:border-primary focus:outline-none"
            placeholder="Tell contractors about yourself..."
          />
        </div>

        {/* Trades */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Wrench className="h-4 w-4 text-primary" />
            Trades
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {TRADES.map((trade) => (
              <button
                key={trade}
                type="button"
                onClick={() => toggleTrade(trade)}
                className={`rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors active:scale-95 ${
                  selectedTrades.includes(trade)
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {trade}
              </button>
            ))}
          </div>
        </div>

        {/* Experience */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Clock className="h-4 w-4 text-primary" />
            Experience
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {EXPERIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setExperience(opt.value)}
                className={`rounded-xl border-2 px-4 py-3 text-sm font-medium transition-colors active:scale-95 ${
                  experience === opt.value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-primary/50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="mb-6">
          <Label htmlFor="suburb" className="flex items-center gap-2 text-base font-semibold mb-2">
            <MapPin className="h-4 w-4 text-primary" />
            Suburb
          </Label>
          <Input
            id="suburb"
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            placeholder="e.g. Parramatta"
            className="h-14 text-base"
          />
        </div>

        {/* Address */}
        <div className="mb-6">
          <Label htmlFor="address" className="flex items-center gap-2 text-base font-semibold mb-2">
            <MapPin className="h-4 w-4 text-primary" />
            Street Address
          </Label>
          <Input
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. 42 Smith St, Parramatta NSW 2150"
            className="h-14 text-base"
          />
          <p className="mt-1 text-xs text-muted-foreground">Used for your onboarding pack only</p>
        </div>

        {/* ABN */}
        <div className="mb-6">
          <Label htmlFor="abn" className="flex items-center gap-2 text-base font-semibold mb-2">
            <Shield className="h-4 w-4 text-primary" />
            ABN
          </Label>
          <Input
            id="abn"
            value={abn}
            onChange={(e) => setAbn(e.target.value)}
            placeholder="e.g. 51 824 753 556"
            inputMode="numeric"
            className="h-14 text-base"
          />
          <p className="mt-1 text-xs text-muted-foreground">Required if you work as a subcontractor</p>
        </div>

        {/* White Card */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Shield className="h-4 w-4 text-primary" />
            White Card
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="white-card-number" className="text-xs text-muted-foreground">Card Number</Label>
              <Input
                id="white-card-number"
                value={whiteCardNumber}
                onChange={(e) => setWhiteCardNumber(e.target.value)}
                placeholder="Card number"
                className="h-12 text-base mt-1"
              />
            </div>
            <div>
              <Label htmlFor="white-card-expiry" className="text-xs text-muted-foreground">Expiry Date</Label>
              <Input
                id="white-card-expiry"
                type="date"
                value={whiteCardExpiry}
                onChange={(e) => setWhiteCardExpiry(e.target.value)}
                className="h-12 text-base mt-1"
              />
            </div>
          </div>
        </div>

        {/* Availability */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Calendar className="h-4 w-4 text-primary" />
            Availability
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {AVAILABILITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAvailability(opt.value)}
                className={`rounded-xl border-2 px-4 py-3 text-left transition-colors active:scale-95 ${
                  availability === opt.value
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <p className={`text-sm font-semibold ${availability === opt.value ? "text-primary" : ""}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-muted-foreground">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-6">
          <Label className="flex items-center gap-2 text-base font-semibold mb-2">
            <Shield className="h-4 w-4 text-primary" />
            Certifications
          </Label>
          <div className="space-y-2 mb-3">
            {certifications.map((cert) => (
              <div
                key={cert}
                className="flex items-center justify-between rounded-xl border-2 border-border px-4 py-3 text-sm"
              >
                <span>{cert}</span>
                <button
                  type="button"
                  onClick={() => removeCert(cert)}
                  className="text-xs text-muted-foreground hover:text-destructive"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newCert}
              onChange={(e) => setNewCert(e.target.value)}
              placeholder="e.g. White Card"
              className="h-12 text-base"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCert();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addCert}
              disabled={!newCert.trim()}
              className="h-12 px-4"
            >
              Add
            </Button>
          </div>
        </div>
      </main>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-screen-sm p-4">
          <Button
            onClick={handleSave}
            className="h-14 w-full gap-2 text-base font-semibold"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
